import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { formatError } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import BookCard from "../../components/BookCard";
import SubscriptionBanner from "../../components/SubscriptionBanner";
import { Store, Lock, Search, X, Coins, Plus } from "lucide-react";

export default function Storefront() {
    const { user, refreshUser } = useAuth();
    const navigate = useNavigate();
    const [books, setBooks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [busyId, setBusyId] = useState(null);
    const [query, setQuery] = useState("");
    const [requestedIds, setRequestedIds] = useState(new Set());

    const canBorrow = user?.subscription?.can_borrow ?? true;
    const coins = user?.zapp_coins ?? 0;
    const hasCoins = coins > 0;

    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q) return books;
        return books.filter((b) =>
            (b.title || "").toLowerCase().includes(q) ||
            (b.author || "").toLowerCase().includes(q)
        );
    }, [books, query]);

    const load = async () => {
        try {
            const [booksRes, resRes] = await Promise.all([
                api.get("/books"),
                api.get("/reservations/borrowing"),
            ]);
            setBooks(booksRes.data);
            const active = new Set(
                (resRes.data || [])
                    .filter((r) => ["pending", "approved", "borrowed"].includes(r.status))
                    .map((r) => r.book_id)
            );
            setRequestedIds(active);
        } catch (e) {
            toast.error(formatError(e));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []);

    const request = async (book) => {
        setBusyId(book.id);
        try {
            await api.post("/reservations", { book_id: book.id });
            setRequestedIds((s) => {
                const next = new Set(s);
                next.add(book.id);
                return next;
            });
            toast.success(`Reservation requested for "${book.title}"`, {
                description: "Track it in Reservations.",
            });
        } catch (e) {
            const status = e?.response?.status;
            const detail = e?.response?.data?.detail || "";
            if (status === 402) {
                toast.error("Your free trial has ended.", {
                    description: "Activate your membership to continue borrowing.",
                    action: { label: "Pay now", onClick: () => navigate("/app/subscription") },
                });
                setTimeout(() => navigate("/app/subscription"), 900);
            } else if (status === 400 && /Zapp coin/i.test(detail)) {
                toast.error("Not enough Zapp coins.", {
                    description: "Post a book to earn 100 Zapp coins and try again.",
                    action: { label: "Post a book", onClick: () => navigate("/app/my-books") },
                });
            } else {
                toast.error(formatError(e));
            }
        } finally {
            setBusyId(null);
            await refreshUser();
        }
    };

    return (
        <div className="max-w-7xl mx-auto px-6 py-10">
            <div className="mb-6">
                <div className="label-mini">Storefront</div>
                <h1 className="font-serif text-4xl font-semibold">Books your classmates are lending</h1>
                <p className="text-ink/60 mt-2">Request to reserve any book — the owner will approve and share an OTP with you.</p>
            </div>

            <SubscriptionBanner subscription={user?.subscription} />

            {/* Zero-coin nudge */}
            {canBorrow && !hasCoins && (
                <div className="mb-6 rounded-2xl border border-zapp/40 bg-zapp/10 p-4 flex flex-wrap items-center gap-3" data-testid="storefront-coins-nudge">
                    <div className="h-9 w-9 rounded-full bg-zapp/20 text-zapp-dark flex items-center justify-center">
                        <Coins className="h-4 w-4" />
                    </div>
                    <div className="flex-1 min-w-[220px]">
                        <div className="font-semibold text-ink">
                            {coins === 0 ? "You have 0 Zapp coins." : `Your balance is ${coins} Zapp.`}
                        </div>
                        <div className="text-sm text-ink/60">
                            Post a book to earn <span className="font-bold text-zapp-dark">+100 Zapp coins</span> before requesting a reservation.
                        </div>
                    </div>
                    <Link to="/app/my-books" className="btn-primary text-sm py-2 px-4" data-testid="storefront-nudge-post">
                        <Plus className="h-4 w-4" /> Post a book
                    </Link>
                </div>
            )}

            {/* Search */}
            <div className="mb-6 flex items-center gap-2 max-w-md">
                <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-ink/40 pointer-events-none" />
                    <input
                        data-testid="storefront-search-input"
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        placeholder="Search by title or author"
                        className="w-full bg-white border border-parchment rounded-full py-2.5 pl-10 pr-10 text-sm focus:outline-none focus:border-brick transition-colors"
                    />
                    {query && (
                        <button
                            data-testid="storefront-search-clear"
                            onClick={() => setQuery("")}
                            className="absolute right-2 top-1/2 -translate-y-1/2 h-6 w-6 rounded-full flex items-center justify-center text-ink/50 hover:bg-paper"
                            aria-label="Clear search"
                        >
                            <X className="h-3.5 w-3.5" />
                        </button>
                    )}
                </div>
                {query && (
                    <div className="text-xs text-ink/50" data-testid="storefront-search-count">
                        {filtered.length} match{filtered.length !== 1 && "es"}
                    </div>
                )}
            </div>

            {loading ? (
                <div className="text-ink/50">Loading…</div>
            ) : filtered.length === 0 ? (
                query ? (
                    <div className="bg-white border-2 border-dashed border-parchment rounded-2xl p-10 text-center" data-testid="storefront-no-results">
                        <div className="h-12 w-12 mx-auto rounded-full bg-paper flex items-center justify-center mb-3">
                            <Search className="h-5 w-5 text-ink/40" />
                        </div>
                        <div className="font-serif text-lg">No books match &ldquo;{query}&rdquo;</div>
                        <p className="text-sm text-ink/60 mt-1">Try a different title or author.</p>
                    </div>
                ) : (
                    <div className="bg-white border-2 border-dashed border-parchment rounded-2xl p-12 text-center" data-testid="storefront-empty">
                        <div className="h-14 w-14 mx-auto rounded-full bg-paper flex items-center justify-center mb-4">
                            <Store className="h-6 w-6 text-ink/40" />
                        </div>
                        <div className="font-serif text-xl mb-1">No books listed yet</div>
                        <p className="text-sm text-ink/60">Be the first — post a book from &ldquo;My Books&rdquo; and earn 100 Zapp coins.</p>
                    </div>
                )
            ) : (
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                    {filtered.map((b) => (
                        <BookCard
                            key={b.id}
                            book={b}
                            compact
                            action={
                                requestedIds.has(b.id) ? (
                                    <span
                                        data-testid={`storefront-requested-badge-${b.id}`}
                                        className="inline-flex items-center gap-1 rounded-md bg-library/10 text-library text-[10px] px-2 py-1 font-semibold uppercase tracking-wider"
                                    >
                                        Reservation Requested
                                    </span>
                                ) : !canBorrow ? (
                                    <button
                                        data-testid={`storefront-locked-btn-${b.id}`}
                                        onClick={() => navigate("/app/subscription")}
                                        className="btn-primary text-[11px] px-2.5 py-1"
                                        title="Activate membership to borrow"
                                    >
                                        <Lock className="h-3 w-3" /> Pay
                                    </button>
                                ) : !hasCoins ? (
                                    <button
                                        data-testid={`storefront-nocoins-btn-${b.id}`}
                                        onClick={() => navigate("/app/my-books")}
                                        className="btn-primary text-[11px] px-2.5 py-1"
                                        title="Post a book to earn coins"
                                    >
                                        <Plus className="h-3 w-3" /> Post to earn
                                    </button>
                                ) : (
                                    <button
                                        data-testid={`storefront-reserve-btn-${b.id}`}
                                        onClick={() => request(b)}
                                        disabled={busyId === b.id || !b.is_available}
                                        className="btn-accept text-[11px] px-2.5 py-1"
                                    >
                                        {busyId === b.id ? "…" : "Request Reservation"}
                                    </button>
                                )
                            }
                        />
                    ))}
                </div>
            )}
        </div>
    );
}
